<template>
  <div class="jd-root">
    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>
    <!-- Header -->
    <div class="jd-header">
      <div class="jd-header-left">
        <h2 class="jd-title">Journey defaults</h2>
        <p class="jd-subtitle">Set default keyword configuration per journey. The AI applies these silently and surfaces them in the confirmation summary — users can override in chat.</p>
      </div>
      <button class="jd-create-btn" @click="openCreateModal">
        <i class="fa fa-plus"></i> Create journey default
      </button>
    </div>

    <!-- Info banner -->
    <div class="jd-banner">
      <i class="fa fa-info-circle jd-banner-icon"></i>
      Journeys are organised by Country › Customer structure. Engineering has created this structure so the AI can narrow down journey selection without requiring users to remember exact names.
    </div>

    <!-- Toolbar -->
    <div class="jd-toolbar">
      <div class="jd-search-wrap">
        <i class="fa fa-search jd-search-icon"></i>
        <input
          class="jd-search-input"
          type="text"
          placeholder="Search journeys..."
          v-model="searchQuery"
        />
      </div>
      <nv-select class="jd-filter-select" v-model="filterCountry">
        <option value="">All countries</option>
        <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
      </nv-select>
      <nv-select class="jd-filter-select" v-model="filterClient">
        <option value="">All customers</option>
        <option v-for="c in clients" :key="c.id" :value="c.id">{{ c.name }}</option>
      </nv-select>
    </div>

    <!-- Table -->
    <div class="jd-table-wrap">
      <div v-if="loading" class="jd-loading">
        <i class="fa fa-spinner fa-spin"></i>
        <span>Loading journey defaults...</span>
      </div>
      <div v-else-if="filteredGroups.length === 0" class="jd-empty">
        No journey defaults found.
      </div>
      <table v-else class="jd-table">
        <thead>
          <tr>
            <th class="jd-th jd-th-name">Journey Name</th>
            <th class="jd-th jd-th-prefix">Keyword Prefix</th>
            <th class="jd-th">Onboarding</th>
            <th class="jd-th jd-th-prompt">GenAI Prompt</th>
            <th class="jd-th jd-th-bool">NLP Question</th>
            <th class="jd-th jd-th-bool">Auto-Accept T&amp;C</th>
            <th class="jd-th jd-th-bool">Club Optin</th>
            <th class="jd-th">Default Gender</th>
            <th class="jd-th">Referral Priority</th>
            <th class="jd-th">Ownership Filter</th>
            <th class="jd-th">Physical/Virtual</th>
            <th class="jd-th">Medical/Pharmacy</th>
            <th class="jd-th jd-th-bool">QR Code</th>
            <th class="jd-th jd-th-bool">Ref Code</th>
            <th class="jd-th jd-th-wide">UTM Source</th>
            <th class="jd-th jd-th-wide">UTM Medium</th>
            <th class="jd-th jd-th-wide">Channel</th>
            <th class="jd-th jd-th-wide">Tags</th>
            <th class="jd-th jd-th-wide">Keyword Use</th>
            <th class="jd-th jd-th-wide">Keyword Topic</th>
            <th class="jd-th jd-th-wide">Scout Menu</th>
            <th class="jd-th jd-th-wide">Welcome Messages</th>
            <th class="jd-th jd-th-actions"></th>
          </tr>
        </thead>
        <tbody v-for="group in filteredGroups" :key="group.key">
          <tr class="jd-group-row">
              <td colspan="24" class="jd-group-cell">
                <span v-if="group.countryFlag" class="jd-flag">{{ group.countryFlag }}</span>
                <span class="jd-group-label">{{ group.countryName }} › {{ group.clientName }}</span>
              </td>
            </tr>
            <!-- Journey rows -->
            <tr
              v-for="row in group.rows"
              :key="row.id"
              class="jd-row"
              :class="{ 'jd-row--dirty': isDirty(row.id) }"
            >
              <td class="jd-td jd-td-name">{{ row.journeyName }}</td>
              <td class="jd-td jd-td-prefix">
                <div v-if="editingPrefix !== row.id" class="jd-prefix-display">
                  <span class="jd-prefix-badge">{{ row.default_data.keyword_prefix }}</span>
                  <button class="jd-prefix-edit-btn" @click="startEditPrefix(row)" title="Edit prefix">
                    <i class="fa fa-pencil"></i>
                  </button>
                </div>
                <div v-else class="jd-prefix-edit-wrap">
                  <input
                    class="jd-prefix-input"
                    v-model="prefixDraft"
                    maxlength="40"
                    @input="prefixDraft = prefixDraft.toLowerCase().replace(/[^a-z0-9_-]/g, '')"
                    @keyup.enter="confirmPrefix(row)"
                    @keyup.esc="cancelPrefix"
                    :ref="'prefix-' + row.id"
                  />
                  <button class="jd-prefix-confirm-btn" @click="confirmPrefix(row)" title="Confirm"><i class="fa fa-check"></i></button>
                  <button class="jd-prefix-cancel-btn" @click="cancelPrefix" title="Cancel"><i class="fa fa-times"></i></button>
                </div>
              </td>
              <td class="jd-td">
                <nv-select class="jd-select" v-model="row.default_data.onboarding_mode" @change="onRowOnboardingChange(row)">
                  <option value="regular">Regular</option>
                  <option value="gen_ai">Gen AI</option>
                  <option value="skip">Skip</option>
                </nv-select>
              </td>
              <td class="jd-td jd-td-prompt">
                <nv-select
                  class="jd-select"
                  v-model="row.default_data.prompt_id"
                  @change="markDirty(row.id)"
                  :disabled="row.default_data.onboarding_mode !== 'gen_ai'"
                >
                  <option :value="null">— none —</option>
                  <option v-for="p in promptList" :key="p.id" :value="p.id">{{ p.label }}</option>
                </nv-select>
              </td>
              <td class="jd-td jd-td-bool">
                <span
                  class="jd-toggle"
                  :class="{ 'jd-toggle--on': row.default_data.nlp_question }"
                  @click="toggle(row, 'nlp_question')"
                >
                  <span class="jd-toggle-knob"></span>
                </span>
              </td>
              <td class="jd-td jd-td-bool">
                <span
                  class="jd-toggle"
                  :class="{ 'jd-toggle--on': row.default_data.auto_accept_tc }"
                  @click="toggle(row, 'auto_accept_tc')"
                >
                  <span class="jd-toggle-knob"></span>
                </span>
              </td>
              <td class="jd-td jd-td-bool">
                <span
                  class="jd-toggle"
                  :class="{ 'jd-toggle--on': row.default_data.club_optin }"
                  @click="toggle(row, 'club_optin')"
                >
                  <span class="jd-toggle-knob"></span>
                </span>
              </td>
              <td class="jd-td">
                <nv-select class="jd-select" v-model="row.default_data.default_gender" @change="markDirty(row.id)">
                  <option value="ask_user">Ask user</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </nv-select>
              </td>
              <td class="jd-td">
                <nv-select class="jd-select" v-model="row.default_data.referral_priority" @change="markDirty(row.id)">
                  <option :value="null">No priority</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </nv-select>
              </td>
              <td class="jd-td">
                <nv-select class="jd-select" v-model="row.default_data.ownership_filter" @change="markDirty(row.id)">
                  <option value="all">All</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </nv-select>
              </td>
              <td class="jd-td">
                <nv-select class="jd-select" v-model="row.default_data.physical_virtual_medical_filter" @change="markDirty(row.id)">
                  <option value="all">All</option>
                  <option value="physical">Physical</option>
                  <option value="virtual">Virtual</option>
                  <option value="medical">Medical</option>
                </nv-select>
              </td>
              <td class="jd-td">
                <nv-select class="jd-select" v-model="row.default_data.medical_filter" @change="markDirty(row.id)">
                  <option value="all">All</option>
                  <option value="medical">Medical</option>
                  <option value="pharmacy">Pharmacy</option>
                </nv-select>
              </td>
              <td class="jd-td jd-td-bool">
                <span
                  class="jd-toggle"
                  :class="{ 'jd-toggle--on': row.default_data.is_qr_code }"
                  @click="toggle(row, 'is_qr_code')"
                >
                  <span class="jd-toggle-knob"></span>
                </span>
              </td>
              <td class="jd-td jd-td-bool">
                <span
                  class="jd-toggle"
                  :class="{ 'jd-toggle--on': row.default_data.send_referral_code }"
                  @click="toggle(row, 'send_referral_code')"
                >
                  <span class="jd-toggle-knob"></span>
                </span>
              </td>
              <td class="jd-td jd-td-wide">
                <input
                  class="jd-input"
                  type="text"
                  v-model="row.default_data.utm_source"
                  placeholder="UTM source"
                  @input="markDirty(row.id)"
                />
              </td>
              <td class="jd-td jd-td-wide">
                <input
                  class="jd-input"
                  type="text"
                  v-model="row.default_data.utm_medium"
                  placeholder="UTM medium"
                  @input="markDirty(row.id)"
                />
              </td>
              <td class="jd-td jd-td-wide">
                <nv-select class="jd-select" v-model="row.default_data.channel" @change="markDirty(row.id)">
                  <option :value="null">--Choose a Channel--</option>
                  <option v-for="ch in channels" :key="ch.value" :value="ch.value">{{ ch.name }}</option>
                </nv-select>
              </td>
              <td class="jd-td jd-td-wide">
                <input
                  class="jd-input"
                  type="text"
                  v-model="row.default_data.tag"
                  placeholder="Tag"
                  @input="markDirty(row.id)"
                />
              </td>
              <td class="jd-td jd-td-wide">
                <nv-select class="jd-select" v-model="row.default_data.keyword_usage" @change="markDirty(row.id)">
                  <option :value="null">--Choose a Option--</option>
                  <option v-for="opt in keywordUsageList" :key="opt.usage_type" :value="opt.usage_type">{{ opt.labels }}</option>
                </nv-select>
              </td>
              <td class="jd-td jd-td-wide">
                <nv-select class="jd-select" v-model="row.default_data.topic" @change="markDirty(row.id)">
                  <option :value="null">--Choose a Option--</option>
                  <option v-for="opt in topicList" :key="opt.display_name" :value="opt.display_name">{{ opt.display_name }}</option>
                </nv-select>
              </td>
              <td class="jd-td jd-td-wide">
                <nv-select class="jd-select" v-model="row.default_data.scout_menu" @change="markDirty(row.id)">
                  <option :value="null">--Choose a Journey--</option>
                  <option v-for="j in journeys" :key="j.id" :value="j.name">{{ j.name }}</option>
                </nv-select>
              </td>
              <td class="jd-td jd-td-wide">
                <button
                  class="jd-show-messages-btn"
                  type="button"
                  @click="openWelcomeMessagesModal(row.country_id, row.default_data.custom_config, result => { $set(row.default_data, 'custom_messages', result.custom_messages); $set(row.default_data, 'custom_config', result.custom_config); markDirty(row.id) })"
                >Show Messages</button>
              </td>
              <td class="jd-td jd-td-actions">
                <button
                  v-if="deletingId !== row.id"
                  class="jd-delete-btn"
                  @click="confirmDelete(row)"
                  title="Delete journey default"
                >
                  <i class="fa fa-trash"></i>
                </button>
                <span v-else class="jd-delete-confirm">
                  <span class="jd-delete-confirm-text">Delete?</span>
                  <button class="jd-delete-confirm-yes" @click="executeDelete(row)" :disabled="deleteInProgress">
                    <i v-if="deleteInProgress" class="fa fa-spinner fa-spin"></i>
                    <i v-else class="fa fa-check"></i>
                  </button>
                  <button class="jd-delete-confirm-no" @click="cancelDelete" :disabled="deleteInProgress">
                    <i class="fa fa-times"></i>
                  </button>
                </span>
              </td>
            </tr>
        </tbody>
      </table>
    </div>

    <!-- Footer -->
    <div class="jd-footer" v-if="dirtyIds.size > 0">
      <span class="jd-dirty-count">{{ dirtyIds.size }} unsaved change{{ dirtyIds.size !== 1 ? 's' : '' }}</span>
      <button class="jd-discard-btn" @click="discardChanges" :disabled="saving">Discard changes</button>
      <button class="jd-save-btn" @click="saveChanges" :disabled="saving">
        <i v-if="saving" class="fa fa-spinner fa-spin"></i>
        {{ saving ? 'Saving...' : 'Save defaults' }}
      </button>
    </div>

    <!-- Create Journey Default Modal -->
    <transition name="jd-modal-fade">
      <div v-if="showCreateModal" class="jd-modal-overlay" @click.self="closeCreateModal">
        <div class="jd-modal">
          <div class="jd-modal-header">
            <h3 class="jd-modal-title">Create journey default</h3>
            <button class="jd-modal-close" @click="closeCreateModal">&times;</button>
          </div>
          <div class="jd-modal-body">

            <!-- ── Journey selection ── -->
            <div class="jd-form-section-label">Journey</div>
            <div class="jd-form-group">
              <label class="jd-form-label">Country <span class="jd-required">*</span></label>
              <nv-select class="jd-form-select" v-model="createForm.country_id" @change="onCreateCountryChange">
                <option value="">Select country...</option>
                <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Client <span class="jd-required">*</span></label>
              <nv-select class="jd-form-select" v-model="createForm.client_id" :disabled="!createForm.country_id" @change="onCreateClientChange">
                <option value="">Select client...</option>
                <option v-for="c in filteredCreateClients" :key="c.id" :value="c.id">{{ c.name }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Journey <span class="jd-required">*</span></label>
              <nv-select class="jd-form-select" v-model="createForm.journey_id" :disabled="!createForm.client_id" @change="onCreateJourneyChange">
                <option value="">Select journey...</option>
                <option v-for="j in filteredCreateJourneys" :key="j.id" :value="j.id">{{ j.name }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Keyword Prefix <span class="jd-form-hint">(auto-generated, editable)</span></label>
              <input class="jd-form-select" v-model="createForm.keyword_prefix" maxlength="40" placeholder="e.g. con_met_sel" @input="createForm.keyword_prefix = createForm.keyword_prefix.toLowerCase().replace(/[^a-z0-9_-]/g, '')" />
            </div>

            <!-- ── Onboarding ── -->
            <div class="jd-form-section-label">Onboarding Configuration</div>
            <div class="jd-form-group">
              <label class="jd-form-label">Onboarding Mode</label>
              <nv-select class="jd-form-select" v-model="createForm.onboarding_mode" @change="onCreateOnboardingChange">
                <option value="regular">Regular</option>
                <option value="gen_ai">Gen AI</option>
                <option value="skip">Skip</option>
              </nv-select>
            </div>
            <div class="jd-form-group" v-if="createForm.onboarding_mode === 'gen_ai'">
              <label class="jd-form-label">GenAI Prompt</label>
              <nv-select class="jd-form-select" v-model="createForm.prompt_id">
                <option :value="null">Select prompt...</option>
                <option v-for="p in promptList" :key="p.id" :value="p.id">{{ p.label }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group" v-if="createForm.onboarding_mode === 'gen_ai'">
              <label class="jd-form-label">GenAI Ad Context</label>
              <textarea class="jd-form-select jd-form-textarea" v-model="createForm.ad_context" placeholder="Enter keyword context for GenAI" rows="3"></textarea>
            </div>
            <div class="jd-form-group" v-if="createForm.onboarding_mode === 'gen_ai'">
              <label class="jd-form-label">Underage Limit <span class="jd-form-hint">(1–99)</span></label>
              <input class="jd-form-select" type="number" v-model="createForm.underage_limit" min="1" max="99" placeholder="Enter lower age limit" />
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Jump State <span class="jd-form-hint">(conversation stage)</span></label>
              <nv-select class="jd-form-select" v-model="createForm.jump_state" :disabled="!createForm.journey_id || createJourneyStages.length === 0">
                <option :value="null">— none —</option>
                <option v-for="s in createJourneyStages" :key="s.id" :value="s.id">{{ s.label }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Default Language <span class="jd-form-hint">(auto-set by country)</span></label>
              <nv-select class="jd-form-select" v-model="createForm.default_language" :disabled="!createForm.country_id">
                <option value="">Select language...</option>
                <option v-for="l in createLanguageOptions" :key="l.value" :value="l.value">{{ l.name }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label jd-form-label--checkbox">
                <input type="checkbox" v-model="createForm.set_preferred_user_language" />
                Set preferred user language <span class="jd-form-hint">(skip language question during onboarding)</span>
              </label>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label jd-form-label--checkbox">
                <input type="checkbox" v-model="createForm.nlp_question" />
                Enable NLP based onboarding question
              </label>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label jd-form-label--checkbox">
                <input type="checkbox" v-model="createForm.auto_accept_tc" />
                Auto-accept Terms &amp; Conditions
              </label>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label jd-form-label--checkbox">
                <input type="checkbox" v-model="createForm.club_optin" />
                Club OptIn and Language Question
              </label>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Default Gender</label>
              <nv-select class="jd-form-select" v-model="createForm.default_gender">
                <option value="ask_user">Ask user</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </nv-select>
            </div>

            <!-- ── Referral ── -->
            <div class="jd-form-section-label">Referral Configuration</div>
            <div class="jd-form-group">
              <label class="jd-form-label">Referral Display Priority</label>
              <nv-select class="jd-form-select" v-model="createForm.referral_priority">
                <option :value="null">No priority</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Ownership Filter</label>
              <nv-select class="jd-form-select" v-model="createForm.ownership_filter">
                <option value="all">All</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Physical / Virtual Filter</label>
              <nv-select class="jd-form-select" v-model="createForm.physical_virtual_medical_filter">
                <option value="all">All</option>
                <option value="physical">Physical</option>
                <option value="virtual">Virtual</option>
                <option value="medical">Medical</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Medical / Pharmacy Filter</label>
              <nv-select class="jd-form-select" v-model="createForm.medical_filter">
                <option value="all">All</option>
                <option value="medical">Medical</option>
                <option value="pharmacy">Pharmacy</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label jd-form-label--checkbox">
                <input type="checkbox" v-model="createForm.is_qr_code" />
                QR code available
              </label>
            </div>
            <div class="jd-form-group" v-if="createForm.is_qr_code">
              <label class="jd-form-label">QR Code Placement</label>
              <nv-select class="jd-form-select" v-model="createForm.qr_code_placement">
                <option :value="null">— none —</option>
                <option v-for="opt in qrCodePlacementOptions" :key="opt.value" :value="opt.value">{{ opt.name }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label jd-form-label--checkbox">
                <input type="checkbox" v-model="createForm.send_referral_code" />
                Send Referral Code
              </label>
            </div>

            <!-- ── Campaign / UTM ── -->
            <div class="jd-form-section-label">Campaign / UTM</div>
            <div class="jd-form-group">
              <label class="jd-form-label">UTM Source</label>
              <input class="jd-form-select" type="text" v-model="createForm.utm_source" placeholder="e.g. google" />
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">UTM Medium</label>
              <input class="jd-form-select" type="text" v-model="createForm.utm_medium" placeholder="e.g. cpc" />
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">UTM Campaign</label>
              <input class="jd-form-select" type="text" v-model="createForm.utm_campaign" placeholder="e.g. spring_sale" />
            </div>

            <!-- ── Metadata ── -->
            <div class="jd-form-section-label">Keyword Metadata</div>
            <div class="jd-form-group">
              <label class="jd-form-label">Channel</label>
              <nv-select class="jd-form-select" v-model="createForm.channel">
                <option :value="null">--Choose a Channel--</option>
                <option v-for="ch in channels" :key="ch.value" :value="ch.value">{{ ch.name }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Tags</label>
              <input class="jd-form-select" type="text" v-model="createForm.tag" placeholder="e.g. promo, seasonal" />
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Keyword Usage</label>
              <nv-select class="jd-form-select" v-model="createForm.keyword_usage">
                <option :value="null">— select —</option>
                <option v-for="opt in keywordUsageList" :key="opt.usage_type" :value="opt.usage_type">{{ opt.labels }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Keyword Topic</label>
              <nv-select class="jd-form-select" v-model="createForm.topic">
                <option :value="null">— select —</option>
                <option v-for="opt in topicList" :key="opt.display_name" :value="opt.display_name">{{ opt.display_name }}</option>
              </nv-select>
            </div>
            <div class="jd-form-group">
              <label class="jd-form-label">Scout Menu <span class="jd-form-hint">(journey to hand off to)</span></label>
              <nv-select class="jd-form-select" v-model="createForm.scout_menu">
                <option :value="null">— none —</option>
                <option v-for="j in journeys" :key="j.id" :value="j.name">{{ j.name }}</option>
              </nv-select>
            </div>

            <!-- ── Welcome Messages ── -->
            <div class="jd-form-section-label">Default Welcome Messages</div>
            <div class="jd-form-group">
              <label class="jd-form-label jd-form-label--checkbox">
                <input type="checkbox" true-value="yes" false-value="no" v-model="createForm.custom_messages" />
                Use Custom Welcome Messages
              </label>
            </div>
            <div class="jd-form-group jd-form-group--row" v-if="createForm.custom_messages === 'yes'">
              <button
                type="button"
                class="jd-show-messages-btn"
                @click="openWelcomeMessagesModal(createForm.country_id, createForm.custom_config, result => { createForm.custom_messages = result.custom_messages; createForm.custom_config = result.custom_config })"
              >Show Messages</button>
              <span class="jd-form-hint">{{ Object.keys(createForm.custom_config || {}).length }} language(s) configured</span>
            </div>
            <p v-if="createError" class="jd-form-error">{{ createError }}</p>
          </div>
          <div class="jd-modal-footer">
            <button class="jd-discard-btn" @click="closeCreateModal" :disabled="creating">Cancel</button>
            <button class="jd-save-btn" @click="submitCreate" :disabled="creating || !createFormValid">
              <i v-if="creating" class="fa fa-spinner fa-spin"></i>
              {{ creating ? 'Creating...' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Welcome Messages Modal -->
    <transition name="jd-modal-fade">
      <div v-if="showWelcomeModal" class="jd-modal-overlay" @click.self="closeWelcomeMessagesModal">
        <div class="jd-modal">
          <div class="jd-modal-header">
            <h3 class="jd-modal-title">New User Welcome Message</h3>
            <button class="jd-modal-close" @click="closeWelcomeMessagesModal">&times;</button>
          </div>
          <div class="jd-modal-body">
            <p v-if="welcomeModalNoLanguageData" class="jd-form-hint jd-form-hint--block">
              No additional languages are configured for this country yet — you can still set the English welcome message below.
            </p>
            <table class="jd-msg-table" v-if="welcomeModalRows[0]">
              <tbody>
                <tr v-for="langRow in welcomeModalRows[0].language" :key="langRow.langShortcode">
                  <td class="jd-msg-lang">{{ langRow.langName }}</td>
                  <td><textarea class="jd-form-select jd-form-textarea" v-model="langRow.message" rows="3" placeholder="Message"></textarea></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="jd-modal-footer">
            <button class="jd-discard-btn" @click="closeWelcomeMessagesModal">Cancel</button>
            <button class="jd-save-btn" @click="saveWelcomeMessages">Save</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/* eslint-disable */
import api from '../../../../utils/api'
import { channels } from '../../../../config/constants'

export default {
  name: 'JourneyDefaults',
  data () {
    return {
      loading: true,
      saving: false,
      creating: false,
      searchQuery: '',
      filterCountry: '',
      filterClient: '',
      rows: [],
      originalRows: {},
      dirtyIds: new Set(),
      countries: [],
      clients: [],
      journeys: [],
      countryLanguageMap: {},
      promptList: [],
      channels,
      keywordUsageList: [],
      topicList: [],
      qrCodePlacementOptions: [
        { value: 'flyer', name: 'Flyer' },
        { value: 'poster', name: 'Poster' },
        { value: 'website', name: 'Website' },
        { value: 'digital_screen', name: 'Digital Screen' },
        { value: 'other', name: 'Other' }
      ],
      createJourneyStages: [],
      editingPrefix: null,
      prefixDraft: '',
      deletingId: null,
      deleteInProgress: false,
      showCreateModal: false,
      createForm: {
        country_id: '',
        client_id: '',
        journey_id: '',
        keyword_prefix: '',
        onboarding_mode: 'regular',
        prompt_id: null,
        ad_context: '',
        underage_limit: null,
        jump_state: null,
        default_language: '',
        set_preferred_user_language: false,
        nlp_question: false,
        auto_accept_tc: false,
        club_optin: false,
        default_gender: 'ask_user',
        referral_priority: null,
        ownership_filter: 'all',
        physical_virtual_medical_filter: 'all',
        medical_filter: 'all',
        referral_resource: '',
        is_qr_code: false,
        qr_code_placement: null,
        send_referral_code: false,
        channel: null,
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        tag: '',
        keyword_usage: null,
        topic: null,
        scout_menu: null,
        custom_messages: 'no',
        custom_config: {}
      },
      createError: '',
      showWelcomeModal: false,
      welcomeModalLanguages: [],
      welcomeModalRows: [],
      welcomeModalOnSave: null,
      welcomeModalNoLanguageData: false
    }
  },
  computed: {
    groups () {
      const map = {}
      for (const row of this.rows) {
        const key = `${row.country_id}__${row.client_id}`
        if (!map[key]) {
          map[key] = {
            key,
            countryName: row.countryName || 'Unknown Country',
            clientName: row.clientName || 'Unknown Client',
            countryFlag: row.countryFlag || '',
            rows: []
          }
        }
        map[key].rows.push(row)
      }
      return Object.values(map)
    },
    filteredGroups () {
      const q = this.searchQuery.toLowerCase().trim()
      return this.groups
        .map(g => {
          let rows = g.rows
          if (q) rows = rows.filter(r => r.journeyName.toLowerCase().includes(q))
          if (this.filterCountry) rows = rows.filter(r => r.country_id === this.filterCountry)
          if (this.filterClient) rows = rows.filter(r => r.client_id === this.filterClient)
          return { ...g, rows }
        })
        .filter(g => g.rows.length > 0)
    },
    filteredCreateClients () {
      return this.clients
    },
    filteredCreateJourneys () {
      return this.journeys
    },
    createLanguageOptions () {
      return this.languageOptionsForCountryId(this.createForm.country_id)
    },
    createFormValid () {
      return this.createForm.country_id && this.createForm.client_id && this.createForm.journey_id
    }
  },
  methods: {
    async fetchData () {
      this.loading = true
      try {
        const params = {}
        if (this.filterCountry) params.country_id = this.filterCountry
        if (this.filterClient) params.client_id = this.filterClient
        const { status, data } = await api.buildRequest('journey-defaults/all', 'GET', params)
        if (status === 200) {
          const list = Array.isArray(data) ? data : (data && data.payload ? data.payload : [])
          this.rows = list.map(r => ({
            ...r,
            journeyName: r.journey_name || r.journeyName || '',
            countryName: r.country_name || r.countryName || '',
            clientName:  r.client_name  || r.clientName  || '',
            default_data: this.normalizeDefaultData(r.default_data || {})
          }))
          this.snapshotOriginals()
        }
      } catch (e) {
        console.error('Failed to fetch journey defaults', e)
      } finally {
        this.loading = false
      }
    },
    async fetchMeta () {
      try {
        const [cRes, clRes, jRes, langRes, promptRes, usageRes, topicRes] = await Promise.all([
          api.buildRequest('getAllSupportedCountries', 'POST', null),
          api.buildRequest('getCustomerList', 'POST', {}),
          api.buildRequest('app', 'GET', { deployed: true }),
          api.buildRequest('languages', 'GET', null),
          api.buildRequest('getAllPrompts', 'POST', {}),
          api.buildRequest('/getKeywordUsageTypes', 'POST'),
          api.buildRequest('/getTopics', 'POST')
        ])
        if (cRes.status === 200) {
          const list = Array.isArray(cRes.data) ? cRes.data : (cRes.data && cRes.data.payload ? cRes.data.payload : [])
          this.countries = list.map(c => ({ id: c.id, name: c.full_name || c.name, ...c }))
        }
        if (clRes.status === 200) {
          const payload = clRes.data && clRes.data.payload
          const list = Array.isArray(payload) ? payload : (payload && payload.customer ? payload.customer : [])
          this.clients = list
        }
        if (jRes.status === 200) {
          const payload = jRes.data && jRes.data.payload
          const list = Array.isArray(payload) ? payload : (payload && payload.apps ? payload.apps : [])
          this.journeys = list.map(j => ({ id: j.id, name: j.appName || j.name, ...j }))
        }
        if (langRes.status === 200) {
          const map = (langRes.data && langRes.data.payload) || {}
          delete map.Global
          delete map['United States']
          this.countryLanguageMap = map
        }
        if (promptRes.status === 200) {
          const prompts = (promptRes.data && promptRes.data.payload && promptRes.data.payload.prompts) || []
          this.promptList = prompts.map(p => ({ id: p.id, label: p.prompt_name }))
        }
        if (usageRes.status === 200) {
          this.keywordUsageList = usageRes.data.payload || []
        }
        if (topicRes.status === 200) {
          this.topicList = topicRes.data.payload || []
        }
      } catch (e) {
        console.error('Failed to fetch meta', e)
      }
    },
    normalizeDefaultData (d) {
      const nullableFields = ['prompt_id', 'jump_state', 'referral_priority', 'qr_code_placement', 'channel', 'keyword_usage', 'topic', 'scout_menu']
      const out = { ...d, custom_messages: (d && d.custom_messages) || 'no', custom_config: (d && d.custom_config) || {} }
      for (const f of nullableFields) {
        if (out[f] === '' || out[f] === undefined) out[f] = null
      }
      return out
    },
    languageOptionsForCountryId (countryId) {
      if (!countryId) return []
      const country = this.countries.find(c => c.id === countryId)
      if (!country) return []
      // Normalize both sides the same way (underscores<->spaces, case) before comparing —
      // countryLanguageMap keys and country.name don't reliably agree on separator/casing.
      const normalize = s => (s || '').replace(/_/g, ' ').toLowerCase()
      const target = normalize(country.name)
      const key = Object.keys(this.countryLanguageMap).find(k => normalize(k) === target)
      return key ? this.countryLanguageMap[key].map(l => ({ value: l.alias, name: l.name })) : []
    },
    // The languages API's `alias` is already the short DB-style code (e.g. "eng", "hi", "swa") —
    // the exact same code keywords.customConfig/custom_config are keyed by. No remapping needed;
    // this just normalizes casing defensively.
    shortLangCode (langValue) {
      return (langValue || '').toLowerCase()
    },
    openWelcomeMessagesModal (countryId, existingCustomConfig, onSave) {
      const langs = this.languageOptionsForCountryId(countryId)
      this.welcomeModalLanguages = langs.some(l => l.value === 'eng') ? langs : [{ value: 'eng', name: 'English' }, ...langs]
      this.welcomeModalNoLanguageData = langs.length === 0

      // Only the single welcome-message slot the entry-points chat actually consumes —
      // language×message table for "sms_welcome1" ("New User Welcome Message").
      this.welcomeModalRows = [{
        name: 'sms_welcome1',
        language: this.welcomeModalLanguages.map(l => {
          const code = this.shortLangCode(l.value)
          const message = (existingCustomConfig && existingCustomConfig[code] && existingCustomConfig[code].sms_welcome1) || ''
          return { langShortcode: l.value, langName: l.name, message }
        })
      }]
      this.welcomeModalOnSave = onSave
      this.showWelcomeModal = true
    },
    closeWelcomeMessagesModal () {
      this.showWelcomeModal = false
      this.welcomeModalOnSave = null
    },
    saveWelcomeMessages () {
      // Mirrors NewKeywordsDetails.vue's simplifyConfigData exactly: flatten row×language
      // back into { [langShortcode]: { [messageTypeName]: message } }.
      const custom_config = {}
      this.welcomeModalRows.forEach(row => {
        row.language.forEach(langRow => {
          const msg = (langRow.message || '').trim()
          if (!msg) return
          const code = this.shortLangCode(langRow.langShortcode)
          if (!custom_config[code]) custom_config[code] = {}
          custom_config[code][row.name] = msg
        })
      })
      const custom_messages = Object.keys(custom_config).length ? 'yes' : 'no'
      if (this.welcomeModalOnSave) this.welcomeModalOnSave({ custom_messages, custom_config })
      this.closeWelcomeMessagesModal()
    },
    snapshotOriginals () {
      this.originalRows = {}
      for (const row of this.rows) {
        this.originalRows[row.id] = JSON.stringify(row.default_data)
      }
    },
    isDirty (id) {
      return this.dirtyIds.has(id)
    },
    markDirty (id) {
      this.dirtyIds = new Set([...this.dirtyIds, id])
    },
    toggle (row, field) {
      row.default_data[field] = !row.default_data[field]
      this.markDirty(row.id)
    },
    discardChanges () {
      for (const id of this.dirtyIds) {
        const row = this.rows.find(r => r.id === id)
        if (row && this.originalRows[id]) {
          row.default_data = JSON.parse(this.originalRows[id])
        }
      }
      this.dirtyIds = new Set()
    },
    async saveChanges () {
      this.saving = true
      try {
        const dirty = this.rows.filter(r => this.dirtyIds.has(r.id))
        const updates = dirty.map(r => ({ id: r.id, default_data: r.default_data }))
        await api.buildRequest('journey-defaults', 'PUT', { updates })
        this.snapshotOriginals()
        this.dirtyIds = new Set()
        this.$swal({ type: 'success', text: 'Journey defaults saved successfully.' })
      } catch (e) {
        console.error('Failed to save journey defaults', e)
        this.$swal({ type: 'error', text: 'Failed to save changes. Please try again.' })
      } finally {
        this.saving = false
      }
    },
    openCreateModal () {
      this.createForm = {
        country_id: '', client_id: '', journey_id: '',
        keyword_prefix: '', onboarding_mode: 'regular', prompt_id: null,
        ad_context: '', underage_limit: null, jump_state: null,
        default_language: '', set_preferred_user_language: false,
        nlp_question: false, auto_accept_tc: false, club_optin: false,
        default_gender: 'ask_user',
        referral_priority: null, ownership_filter: 'all',
        physical_virtual_medical_filter: 'all', medical_filter: 'all',
        referral_resource: '', is_qr_code: false, qr_code_placement: null,
        send_referral_code: false,
        channel: null,
        utm_source: '', utm_medium: '', utm_campaign: '',
        tag: '', keyword_usage: null, topic: null, scout_menu: null,
        custom_messages: 'no', custom_config: {}
      }
      this.createError = ''
      this.createJourneyStages = []
      this.showCreateModal = true
    },
    closeCreateModal () {
      this.showCreateModal = false
    },
    splitWords (str) {
      return str
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/[_\-]/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
    },
    abbreviateName (str) {
      const words = this.splitWords(str)
      if (words.length === 1) return words[0].substring(0, 3).toUpperCase()
      return words.map(w => w[0].toUpperCase()).join('')
    },
    generateKeywordPrefix () {
      const country = this.countries.find(c => c.id === this.createForm.country_id)
      const client = this.clients.find(c => c.id === this.createForm.client_id)
      const journey = this.journeys.find(j => j.id === this.createForm.journey_id)
      if (!country || !client || !journey) return
      const countryCode = (country.name || '').substring(0, 3).toLowerCase()
      const clientCode = this.abbreviateName(client.name || '').toLowerCase()
      const journeyCode = this.abbreviateName(journey.name || '').toLowerCase()
      this.createForm.keyword_prefix = `${countryCode}_${clientCode}_${journeyCode}_`
    },
    onRowOnboardingChange (row) {
      if (row.default_data.onboarding_mode !== 'gen_ai') {
        row.default_data.prompt_id = ''
      }
      this.markDirty(row.id)
    },
    onCreateOnboardingChange () {
      if (this.createForm.onboarding_mode !== 'gen_ai') {
        this.createForm.prompt_id = ''
        this.createForm.ad_context = ''
        this.createForm.underage_limit = ''
      }
    },
    onCreateCountryChange () {
      this.createForm.client_id = ''
      this.createForm.journey_id = ''
      this.createForm.keyword_prefix = ''
      const langs = this.createLanguageOptions
      this.createForm.default_language = langs.length ? langs[0].value : ''
    },
    onCreateClientChange () {
      this.createForm.journey_id = ''
      this.createForm.keyword_prefix = ''
    },
    onCreateJourneyChange () {
      const journey = this.journeys.find(j => j.id === this.createForm.journey_id)
      this.createForm.jump_state = ''
      const stages = journey && journey.config && journey.config.stages
      this.createJourneyStages = stages
        ? Object.keys(stages).map(key => ({ id: key, label: stages[key].stageName || key }))
        : []
      this.generateKeywordPrefix()
    },
    startEditPrefix (row) {
      this.editingPrefix = row.id
      this.prefixDraft = (row.default_data.keyword_prefix || '').toLowerCase()
      this.$nextTick(() => {
        const el = this.$refs['prefix-' + row.id]
        if (el) (Array.isArray(el) ? el[0] : el).focus()
      })
    },
    confirmPrefix (row) {
      row.default_data.keyword_prefix = this.prefixDraft.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40)
      this.markDirty(row.id)
      this.editingPrefix = null
    },
    cancelPrefix () {
      this.editingPrefix = null
    },
    confirmDelete (row) {
      this.deletingId = row.id
    },
    cancelDelete () {
      this.deletingId = null
    },
    async executeDelete (row) {
      this.deleteInProgress = true
      try {
        const { status } = await api.buildRequest(`journey-defaults/${row.id}`, 'DELETE', null)
        if (status === 200) {
          this.rows = this.rows.filter(r => r.id !== row.id)
          this.dirtyIds.delete(row.id)
          this.dirtyIds = new Set(this.dirtyIds)
          delete this.originalRows[row.id]
        }
      } catch (e) {
        console.error('Failed to delete journey default', e)
      } finally {
        this.deletingId = null
        this.deleteInProgress = false
      }
    },
    async submitCreate () {
      if (!this.createFormValid) return
      this.creating = true
      this.createError = ''
      try {
        const f = this.createForm
        const { status, data } = await api.buildRequest('journey-defaults', 'POST', {
          country_id: f.country_id,
          client_id: f.client_id,
          journey_id: f.journey_id,
          default_data: {
            keyword_prefix: f.keyword_prefix,
            default_language: f.default_language,
            set_preferred_user_language: f.set_preferred_user_language,
            onboarding_mode: f.onboarding_mode || 'regular',
            prompt_id: f.onboarding_mode === 'gen_ai' ? f.prompt_id : null,
            ad_context: f.onboarding_mode === 'gen_ai' ? f.ad_context : '',
            underage_limit: f.onboarding_mode === 'gen_ai' ? (f.underage_limit || null) : null,
            jump_state: f.jump_state || '',
            nlp_question: f.nlp_question,
            auto_accept_tc: f.auto_accept_tc,
            club_optin: f.club_optin,
            default_gender: f.default_gender || 'ask_user',
            referral_priority: f.referral_priority,
            ownership_filter: f.ownership_filter || 'all',
            physical_virtual_medical_filter: f.physical_virtual_medical_filter || 'all',
            medical_filter: f.medical_filter || 'all',
            referral_resource: f.referral_resource || '',
            is_qr_code: f.is_qr_code,
            qr_code_placement: f.is_qr_code ? (f.qr_code_placement || '') : '',
            send_referral_code: f.send_referral_code,
            channel: f.channel || '',
            utm_source: f.utm_source || '',
            utm_medium: f.utm_medium || '',
            utm_campaign: f.utm_campaign || '',
            tag: f.tag || '',
            keyword_usage: f.keyword_usage || '',
            topic: f.topic || '',
            scout_menu: f.scout_menu || '',
            custom_messages: f.custom_messages === 'yes' ? 'yes' : 'no',
            custom_config: f.custom_messages === 'yes' ? (f.custom_config || {}) : {}
          }
        })
        if (status === 200 || status === 201) {
          this.closeCreateModal()
          await this.fetchData()
        } else {
          this.createError = (data && data.message) || 'Failed to create journey default.'
        }
      } catch (e) {
        this.createError = 'An error occurred. Please try again.'
        console.error(e)
      } finally {
        this.creating = false
      }
    }
  },
  mounted () {
    this.fetchMeta()
    this.fetchData()
  }
}
/* eslint-enable */
</script>
<style scoped src="./JourneyDefaults.css"></style>



// WEBPACK FOOTER //
// JourneyDefaults.vue?1610ec67